import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { IDSButton, IDSRadio, IDSRadioGroup, IDSInput, IDSTextarea, IDSAlert, IDSDatePicker, IDSTooltip, IDSSpinner, IDSCard, IDSBadge, IDSSelect } from "@inera/ids-react";
import InfoTooltip from "../../common/InfoTooltip";
import MarkdownContent from "../../common/MarkdownContent";
import LinkifiedText from "../../common/LinkifiedText";
import Collapse from "../../Collapse";
import AutosaveIndicator from "../../common/AutosaveIndicator";
import LastUpdated from "../../common/LastUpdated";
import ConfirmDialog from "../../ConfirmDialog";
import { getErrorMessage } from "../../../utils/appErrorMessages";
import { useAutosave } from "../../../hooks/useAutosave";
import { fetchQuestions, fetchQuestionAnswers, saveQuestionAnswers, saveJustification, importQuestionAnswers } from "../../../services/testResultsAPI";
import { setTestExecutionStatus, createManualDeclarationExecution } from "../../../services/testExecutionAPI";
import { fetchSourceSystemOptions } from "../../../services/serviceOrderAPI";

// Question types whose answer is stored in answerText rather than as a yes/no.
const TEXT_VALUE_TYPES = ["TEXT", "DATE", "SOURCE_SYSTEM", "SERVICE_PROVIDER"];
const isTextValueType = (questionType) => TEXT_VALUE_TYPES.includes(questionType);

/**
 * Splits a stored källsystem answer back into the picked parent option and the free-text
 * undernivå. The answer text is the source of truth — the picker keeps no state of its
 * own — so the longest matching parentLabel wins, otherwise a parent "Cosmic" would
 * swallow a value that belongs to a parent "Cosmic R8".
 */
const splitSourceSystemValue = (options, value) => {
  if (!value) return { parentId: "", child: "" };
  let best = null;
  for (const option of options) {
    const label = option.parentLabel;
    if (value === label || value.startsWith(`${label} `)) {
      if (!best || label.length > best.parentLabel.length) best = option;
    }
  }
  if (!best) return { parentId: "", child: "" };
  // Only the leading separator is stripped; a trailing space the tester is still typing
  // has to survive the round-trip through the answer text.
  return { parentId: best.parentId, child: value.slice(best.parentLabel.length).replace(/^\s+/, "") };
};

const composeSourceSystemValue = (parentLabel, child) => (child ? `${parentLabel} ${child}` : parentLabel);

const SourceSystemPicker = ({ value, options, loading, error, onChange }) => {
  // Without the option list the stored answer cannot be mapped to a selection, so show it
  // as-is rather than rendering an empty dropdown that looks like a lost answer.
  if (error) {
    return (
      <>
        <IDSAlert type="error" compact><p>{error}</p></IDSAlert>
        {value && <p className="ids-mt-2">Sparat svar: {value}</p>}
      </>
    );
  }

  const { parentId, child } = splitSourceSystemValue(options, value);
  const parent = options.find((option) => option.parentId === parentId);

  const handleParentChange = (e) => {
    const next = options.find((option) => option.parentId === e.target.value);
    onChange(next ? composeSourceSystemValue(next.parentLabel, child) : "");
  };

  const handleChildChange = (e) => {
    if (!parent) return;
    onChange(composeSourceSystemValue(parent.parentLabel, e.target.value));
  };

  return (
    <>
      <IDSSelect
        label="Källsystem"
        value={parentId}
        onChange={handleParentChange}
        disabled={loading}
      >
        <option value="">{loading ? "Laddar källsystem..." : "Välj källsystem"}</option>
        {options.map((option) => (
          <option key={option.parentId} value={option.parentId}>{option.parentLabel}</option>
        ))}
      </IDSSelect>

      <div className="ids-mt-3">
        <IDSInput
          type="text"
          label="Version/steg"
          maxLength={255}
          value={child}
          disabled={!parent}
          onChange={handleChildChange}
          onBlur={() => parent && onChange(composeSourceSystemValue(parent.parentLabel, child).trim())}
          placeholder={parent ? "t.ex. R8.3" : "Välj källsystem först"}
        />
      </div>
    </>
  );
};

/**
 * QuestionSection component that displays contract-specific questions
 * with yes/no answers and optional follow-up text inputs.
 */
const QuestionSection = ({ contractId, organisationId, executionId, executionStatus, onAnswersChange, allFailedTestsJustified, testResults, justifications, scenario = 'webapp', resetSignal }) => {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importSuccess, setImportSuccess] = useState(null);
    const [importUnmatchedQuestions, setImportUnmatchedQuestions] = useState([]);
    const [importMissingMotivationQuestionIds, setImportMissingMotivationQuestionIds] = useState([]);
    const importInputRef = useRef(null);

    const isInReview = executionStatus === 'PENDING_REVIEW' || executionStatus === 'IN_REVIEW';
    const [submittingReport, setSubmittingReport] = useState(false);
    const submittingReportRef = useRef(false);
    // Submitting is irreversible and only one declaration per organisation is accepted,
    // so the user must confirm before the report is sent for review.
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceSystemOptions, setSourceSystemOptions] = useState([]);
  const [sourceSystemsLoading, setSourceSystemsLoading] = useState(false);
  const [sourceSystemsError, setSourceSystemsError] = useState(null);
  // Latest save across all sections for this user's own answers. Seeded from the
  // server on load and refreshed after every save, manual or automatic.
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // Sections the user has actually touched. Autosave only writes these, so a user
  // who just has the form open never rewrites sections they never edited.
  const [dirtySections, setDirtySections] = useState([]);
  const [optionalCommentVisible, setOptionalCommentVisible] = useState({});
  const [openSections, setOpenSections] = useState({});
  // Set when the server reports MIMIR-3004 (a race on the per-user unique constraint).
  // Blocks autosave until the user reloads,
  // so a conflict is never silently retried and overwritten 10 seconds later.
  const [conflict, setConflict] = useState(null);

  const sectionIdByQuestion = useMemo(() => {
    const map = {};
    sections.forEach((section) => {
      section.questions?.forEach((q) => { map[q.id] = section.id; });
    });
    return map;
  }, [sections]);

  // A new array is produced on every change, so the autosave debounce restarts
  // on each keystroke even when the section is already marked dirty.
  const markDirty = useCallback((questionId) => {
    const sectionId = sectionIdByQuestion[questionId];
    if (sectionId === undefined) return;
    setDirtySections((prev) => (prev.includes(sectionId) ? [...prev] : [...prev, sectionId]));
  }, [sectionIdByQuestion]);

    // Extracted as useCallback so useAutosave can hold a stable reference.
    // Declared after sections/answers state so they are in scope.
    const performSave = useCallback(async ({ onlyDirty = false } = {}) => {
      if (!contractId || !organisationId) return;
      const targets = onlyDirty
        ? sections.filter((section) => dirtySections.includes(section.id))
        : sections;
      if (targets.length === 0) return;

      // Clear up front so edits made while the request is in flight stay marked
      // dirty and get picked up by the next save.
      const targetIds = targets.map((section) => section.id);
      setDirtySections((prev) => prev.filter((id) => !targetIds.includes(id)));

      let latest = null;
      try {
        for (const section of targets) {
          const answersForSection = section.questions
            .map(q => {
              const ans = answers[q.id];
              if (isTextValueType(q.questionType)) {
                return {
                  questionId: q.id,
                  answerBoolean: null,
                  answerText: ans?.textValue || "",
                  motivation: null,
                };
              }
              if (ans?.answer === null || ans === undefined) return null;
              const savedMotivation = (ans.motivation ?? '').trim();
              return {
                questionId: q.id,
                answerBoolean: typeof ans.answer === 'boolean' ? ans.answer : null,
                answerText: ans.followupText || "",
                motivation: savedMotivation || null,
              };
            })
            .filter(a => a !== null);
          if (answersForSection.length > 0) {
            const saved = await saveQuestionAnswers(
              contractId,
              section.id,
              organisationId,
              { answers: answersForSection }
            );
            const stamp = saved?.updatedAt;
            if (stamp && (!latest || new Date(stamp) > new Date(latest))) {
              latest = stamp;
            }
          }
        }
      } catch (err) {
        // Put the sections back so a failed save is retried rather than silently dropped.
        setDirtySections((prev) => [...new Set([...prev, ...targetIds])]);
        if (err?.status === 409) {
          setConflict(getErrorMessage(err.errorCode, err.origin) || err.message);
        }
        throw err;
      }
      if (latest) {
        setLastUpdatedAt(latest);
      }
    }, [contractId, organisationId, sections, answers, dirtySections]);

    // Manual save wrapper — keeps the existing save button working
    const handleSaveAnswers = async () => {
      if (!contractId || !organisationId) return;
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);
      try {
        await performSave();
        setSaveSuccess(true);
      } catch (err) {
        setSaveError(err.message || "Kunde inte spara svar.");
      } finally {
        setSaving(false);
      }
    };

    // Performs the actual submission. Only called after the user confirms, since
    // submitting is irreversible and an organisation may only have one declaration
    // under review at a time.
    const performSubmit = async () => {
      if (submittingReportRef.current) {
        return;
      }
      submittingReportRef.current = true;
      flushSync(() => {
        setSubmittingReport(true);
        setSubmitError(null);
      });
      try {
        // First save all justifications from test results
        if (testResults && testResults.length > 0 && justifications) {
          await Promise.all(
            testResults.map(async (test) => {
              const key = `${test.testSuite}-${test.testCase}`;
              const justification = justifications[key] || '';
              await saveJustification(test.id, justification);
            })
          );
        }
        // Then save question answers. Call the raw save operation here:
        // handleSaveAnswers is the manual-button wrapper and deliberately
        // converts errors into UI state, which would otherwise let report
        // submission continue with partially persisted answers.
        await performSave();
        // Ensure there is an execution to submit. The manual (self-declaration)
        // flow has no automated test run, so no execution exists yet — create a
        // questionnaire-only one before marking the report as submitted.
        let execId = executionId;
        if (!execId) {
          const created = await createManualDeclarationExecution(contractId, organisationId);
          execId = created.id;
        }
        // Set execution status to PENDING_REVIEW to mark the report as submitted
        await setTestExecutionStatus(execId, 'PENDING_REVIEW');
        // Finally navigate to reports
        navigate("/testrapporter");
      } catch (err) {
        console.error('Error saving data:', err);
        setSubmitError('Kunde inte spara alla data. Försök igen.');
        submittingReportRef.current = false;
        setSubmittingReport(false);
        // Close the dialog so the error message below the button is visible
        setShowSubmitConfirm(false);
      }
    };

    // Autosave: fires 10 s after the last change, and only writes the sections the
    // user actually edited. Disabled while in review or when nothing is dirty.
    const autosaveDirty = useCallback(() => performSave({ onlyDirty: true }), [performSave]);

    const autosaveStatus = useAutosave(
      autosaveDirty,
      [answers, dirtySections],
      {
        delay: 10000,
        enabled: !isInReview && !submittingReport && !!contractId && !!organisationId
          && dirtySections.length > 0 && !conflict,
      }
    );

  // Scroll targets for each section card, keyed by section id, so the
  // navigator on the right can jump to a section on click.
  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const scrollToQuestion = (questionId) => {
    const element = typeof document !== 'undefined'
      ? document.getElementById(`question-${questionId}`)
      : null;
    if (!element) return;
    element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    element.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!contractId) return;

    const loadQuestions = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchQuestions(contractId);
        setSections(data);
        setOpenSections((prev) => {
          const next = {};
          data.forEach((section, index) => {
            next[section.id] = prev[section.id] ?? index === 0;
          });
          return next;
        });
        
        // Initialize answers state — include textValue for TEXT-type questions
        const initialAnswers = {};
        data.forEach((section) => {
          section.questions?.forEach((q) => {
            if (q.questionType === 'TEXT_BLOCK') return; // no answer needed
            initialAnswers[q.id] = { answer: null, followupText: "", textValue: "", motivation: "", answerId: null, questionType: q.questionType, mandatory: q.mandatory !== false };
          });
        });

        // Fetch stored answers for each section and merge with initial state
        let latestUpdate = null;
        if (organisationId) {
          for (const section of data) {
            try {
              const storedAnswers = await fetchQuestionAnswers(contractId, section.id, organisationId);
              const stamp = storedAnswers?.updatedAt;
              if (stamp && (!latestUpdate || new Date(stamp) > new Date(latestUpdate))) {
                latestUpdate = stamp;
              }
              if (storedAnswers && storedAnswers.answers) {
                storedAnswers.answers.forEach((ans) => {
                  if (initialAnswers[ans.questionId] !== undefined) {
                    initialAnswers[ans.questionId] = {
                      ...initialAnswers[ans.questionId],
                      answer: ans.answerBoolean,
                      followupText: ans.answerText || "",
                      textValue: ans.answerText || "",
                      motivation: ans.motivation || "",
                      answerId: ans.id ?? null,
                    };
                  }
                });
              }
            } catch (err) {
              console.warn(`Could not load answers for section ${section.id}:`, err);
            }
          }
        }

        setAnswers(initialAnswers);
        setLastUpdatedAt(latestUpdate);
      } catch (err) {
        setError("Kunde inte ladda frågorna. Försök igen eller kontakta support.");
        console.error("Error fetching questions:", err);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [contractId, organisationId]);

  // The källsystem options come from Jira; only fetch them when a section actually asks
  // for one, so contracts without the Anslutningsuppgifter section pay nothing for it.
  const hasSourceSystemQuestion = sections.some(
    (section) => (section.questions || []).some((q) => q.questionType === 'SOURCE_SYSTEM')
  );

  useEffect(() => {
    if (!hasSourceSystemQuestion) return;
    setSourceSystemsLoading(true);
    setSourceSystemsError(null);
    fetchSourceSystemOptions()
      .then(setSourceSystemOptions)
      .catch((err) => setSourceSystemsError(err?.message || "Kunde inte hämta källsystem."))
      .finally(() => setSourceSystemsLoading(false));
  }, [hasSourceSystemQuestion]);

  // Notify parent when answers change
  useEffect(() => {
    if (onAnswersChange) {
      onAnswersChange(answers);
    }
  }, [answers, onAnswersChange]);

  // Persists a full reset to the backend. Unlike the normal (auto)save, which
  // omits unanswered questions on purpose (so an idle tab never erases an
  // answer another tab just wrote), a reset must explicitly clear every
  // question so the blanked view survives a reload instead of the old
  // stored answers reappearing.
  const performReset = useCallback(async () => {
    if (!contractId || !organisationId) return;
    for (const section of sections) {
      const answersForSection = (section.questions || [])
        .filter((q) => q.questionType !== 'TEXT_BLOCK')
        .map((q) => ({
          questionId: q.id,
          answerBoolean: null,
          answerText: "",
          motivation: "",
        }));
      if (answersForSection.length > 0) {
        await saveQuestionAnswers(contractId, section.id, organisationId, { answers: answersForSection });
      }
    }
  }, [contractId, organisationId, sections]);

  // "Återställ vy": clear all currently displayed answers back to blank and
  // immediately persist that reset (rather than waiting for the autosave
  // debounce), so navigating away right after a reset doesn't leave the old
  // answers stored on the server. Skip the very first render so mounting
  // doesn't wipe answers just fetched from the server.
  const isFirstResetSignal = useRef(true);
  useEffect(() => {
    if (isFirstResetSignal.current) {
      isFirstResetSignal.current = false;
      return;
    }
    const blanked = {};
    Object.keys(answers).forEach((questionId) => {
      blanked[questionId] = { ...answers[questionId], answer: null, followupText: "", textValue: "", motivation: "", answerId: null };
    });
    setAnswers(blanked);
    setDirtySections([]);
    setSaveError(null);
    setSaveSuccess(false);
    setSubmitError(null);
    setConflict(null);
    setImportResult(null);
    setImportError(null);
    setImportSuccess(null);
    setImportUnmatchedQuestions([]);
    setImportMissingMotivationQuestionIds([]);

    setSaving(true);
    performReset()
      .then(() => setSaveSuccess(true))
      .catch((err) => {
        console.error('Error autosaving after reset', err);
        setSaveError(err.message || "Kunde inte spara den återställda vyn.");
      })
      .finally(() => setSaving(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  // Auto-dismiss the "saved" confirmation after a short delay.
  useEffect(() => {
    if (!saveSuccess) return;
    const timer = setTimeout(() => setSaveSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [saveSuccess]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answer: value,
        followupText: value === true ? "" : prev[questionId]?.followupText || "",
      },
    }));
    setOptionalCommentVisible((prev) => ({
      ...prev,
      [questionId]: false,
    }));
    markDirty(questionId);
  };

  const handleFollowupChange = (questionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        followupText: text,
      },
    }));
    markDirty(questionId);
  };

  const handleTextValueChange = (questionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textValue: text,
      },
    }));
    markDirty(questionId);
  };

  const handleMotivationChange = (questionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        motivation: text,
      },
    }));
    markDirty(questionId);
  };

  const hasAnswer = (question, answer) => {
    if (!answer) return false;
    if (question.questionType === 'TEXT' || question.questionType === 'DATE') {
      return Boolean((answer.textValue || '').trim());
    }
    return answer.answer === true || answer.answer === false;
  };

  const findQuestion = (questionId) => {
    for (const section of sections) {
      const question = section.questions?.find((candidate) => candidate.id === questionId);
      if (question) return question;
    }
    return null;
  };

  const availableImportedAnswers = importResult?.answers?.filter((imported) => {
    const question = findQuestion(imported.questionId);
    return question && !hasAnswer(question, answers[imported.questionId]);
  }) || [];

  const importableAnswerCount = availableImportedAnswers.length;

  const existingImportedAnswerCount = importResult
    ? importResult.answers.length - availableImportedAnswers.length
    : 0;

  const unansweredImportedQuestions = importUnmatchedQuestions.filter((unmatched) => {
    const question = findQuestion(unmatched.questionId);
    return question && !hasAnswer(question, answers[unmatched.questionId]);
  });

  const importedQuestionsMissingMotivation = importMissingMotivationQuestionIds
    .map((questionId) => findQuestion(questionId))
    .filter((question) => {
      if (!question) return false;
      const currentAnswer = answers[question.id]?.answer;
      const stillUnexpected =
        (currentAnswer === true || currentAnswer === false) &&
        question.expectedAnswerBoolean !== null &&
        question.expectedAnswerBoolean !== undefined &&
        currentAnswer !== question.expectedAnswerBoolean;
      return stillUnexpected && !((answers[question.id]?.motivation || '').trim());
    });

  const handleWordImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportError(null);
    setImportResult(null);
    setImportSuccess(null);
    setImportUnmatchedQuestions([]);
    setImportMissingMotivationQuestionIds([]);

    if (!file.name.toLowerCase().endsWith('.docx')) {
      setImportError('Endast Word-dokument i formatet .docx kan importeras.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setImportError('Filen är för stor — max 10 MB.');
      return;
    }

    try {
      setImporting(true);
      const result = await importQuestionAnswers(contractId, organisationId, file);
      setImportResult(result);
      const hasImportableAnswers = (result.answers || []).some((imported) => {
        const question = findQuestion(imported.questionId);
        return question && !hasAnswer(question, answers[imported.questionId]);
      });
      setImportUnmatchedQuestions(
        hasImportableAnswers ? [] : (result.unmatchedQuestions || [])
      );
    } catch (err) {
      setImportError(err.message || 'Word-dokumentet kunde inte importeras.');
    } finally {
      setImporting(false);
    }
  };

  const applyImportedAnswers = () => {
    const importableAnswers = (importResult?.answers || []).filter((imported) => {
      const question = findQuestion(imported.questionId);
      return question &&
        !hasAnswer(question, answers[imported.questionId]);
    });
    setImportMissingMotivationQuestionIds(
      importableAnswers
        .filter((imported) => {
          const question = findQuestion(imported.questionId);
          return question?.questionType === 'YES_NO' &&
            (imported.answerBoolean === true || imported.answerBoolean === false) &&
            question.expectedAnswerBoolean !== null &&
            question.expectedAnswerBoolean !== undefined &&
            imported.answerBoolean !== question.expectedAnswerBoolean &&
            !((imported.answerText || '').trim());
        })
        .map((imported) => imported.questionId)
    );
    setAnswers((previous) => {
      const updated = { ...previous };
      for (const imported of importableAnswers) {
        const question = findQuestion(imported.questionId);
        const current = updated[imported.questionId];

        const next = { ...current };
        if (question.questionType === 'TEXT' || question.questionType === 'DATE') {
          next.textValue = imported.answerText || '';
        } else if (imported.answerBoolean === true || imported.answerBoolean === false) {
          const comment = imported.answerText || '';
          const trigger = question.followupConfig?.trigger || 'NONE';
          const followupTriggered =
            (trigger === 'ON_YES' && imported.answerBoolean === true) ||
            (trigger === 'ON_NO' && imported.answerBoolean === false);
          const unexpected =
            question.expectedAnswerBoolean !== null &&
            question.expectedAnswerBoolean !== undefined &&
            imported.answerBoolean !== question.expectedAnswerBoolean;
 
          next.answer = imported.answerBoolean;
          if (imported.answerBoolean === true) {
            next.followupText = '';
          } else if (followupTriggered || (!followupTriggered && !unexpected)) {
            next.followupText = comment;
          }
          if (unexpected) {
            next.motivation = comment;
          }
        } else {
          continue;
        }
        updated[imported.questionId] = next;
      }
      return updated;
    });
    setImportUnmatchedQuestions(importResult?.unmatchedQuestions || []);
    setImportResult(null);
    // Imported answers must be autosaved like manually typed ones, otherwise they
    // stay in the browser until the tester happens to edit another field.
    importableAnswers.forEach((imported) => markDirty(imported.questionId));
    setImportSuccess(`${importableAnswers.length} svar fylldes i. Granska svaren innan du skickar in.`);
  };

  const isAnswerUnexpected = (question, answer) => {
    return (
      question.expectedAnswerBoolean !== null &&
      question.expectedAnswerBoolean !== undefined &&
      answer !== null &&
      answer !== undefined &&
      answer !== question.expectedAnswerBoolean
    );
  };

  const isFollowupVisible = (question, answer) => {
    if (!question.followupConfig || question.followupConfig.trigger === "NONE") {
      return false;
    }
    if (question.followupConfig.trigger === "ON_YES" && answer === true) {
      return true;
    }
    if (question.followupConfig.trigger === "ON_NO" && answer === false) {
      return true;
    }
    return false;
  };

  if (loading) {
    return (
      <div className="ids-flex" style={{ alignItems: "center", gap: "0.5rem" }}>
        <IDSSpinner size="s" />
        <span>Laddar frågor...</span>
      </div>
    );
  }

  if (error) {
    return <IDSAlert type="error"><p>Kunde inte ladda frågor: {error}</p></IDSAlert>;
  }

  if (sections.length === 0) {
    return <IDSAlert type="info"><p>Inga frågor tillgängliga för detta kontrakt.</p></IDSAlert>;
  }

  // Sections shown for the current scenario (same rule as the render below).
  const isSectionVisible = (section) => {
    const s = section.applicableScenario || 'ALL';
    if (s === 'ALL') return true;
    if (scenario === 'local') return s === 'LOCAL_ONLY';
    return s === 'WEBAPP_ONLY'; // webapp (standard + sandbox)
  };

  // Is a single question still incomplete? Besides an unanswered mandatory question,
  // a triggered follow-up input and the motivation for an unexpected answer are both
  // mandatory and block submission until filled in.
  const isAnswerMissing = (q) => {
    if (q.questionType === 'TEXT_BLOCK') return false;
    const ans = answers[q.id] || {};
    if (isTextValueType(q.questionType)) {
      return q.mandatory !== false && !((ans.textValue || '').trim());
    }
    // YES_NO
    const answered = ans.answer === true || ans.answer === false;
    if (q.mandatory !== false && !answered) return true;
    if (!answered) return false; // optional and left unanswered → nothing more required
    // Follow-up text is mandatory when the follow-up is triggered.
    if (isFollowupVisible(q, ans.answer) && !((ans.followupText || '').trim())) return true;
    // Motivation is mandatory when the given answer is unexpected.
    if (isAnswerUnexpected(q, ans.answer) && !((ans.motivation || '').trim())) return true;
    return false;
  };

  // Sections (visible for this scenario) that still have unanswered mandatory questions.
  const missingSections = sections
    .filter(isSectionVisible)
    .map(section => ({
      title: section.title,
      count: (section.questions || []).filter(isAnswerMissing).length,
    }))
    .filter(s => s.count > 0);

  return (
    <div className="ids-mt-6" style={{ width: "100%", display: "flex", gap: "1.5rem", alignItems: "flex-start", flex: 1 }}>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
      {scenario === 'local' && !isInReview && (
        <IDSCard className="ids-mb-6">
          <div>
            <h3 className="ids-heading-s">Importera svar från Word</h3>
            <p className="ids-body">
              Frågorna matchas mot frågetexten i dokumentet. Importerade svar fylls först i
              efter att du har granskat resultatet, och redan ifyllda svar skrivs inte över.
            </p>
            <input
              ref={importInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleWordImport}
              style={{ display: 'none' }}
              aria-label="Välj Word-dokument"
            />
            <IDSButton
              type="button"
              size="m"
              secondary
              disabled={importing}
              onClick={() => importInputRef.current?.click()}
            >
              {importing ? 'Analyserar dokument...' : 'Välj Word-dokument'}
            </IDSButton>

            {importError && (
              <div className="ids-mt-3">
                <IDSAlert type="error"><p>{importError}</p></IDSAlert>
              </div>
            )}
            {importSuccess && (
              <div className="ids-mt-3">
                <IDSAlert type="success"><p>{importSuccess}</p></IDSAlert>
              </div>
            )}
            {unansweredImportedQuestions.length > 0 && (
              <div className="ids-mt-3">
                <IDSAlert type="attention">
                  <p>
                    <strong>
                      {unansweredImportedQuestions.length === 1
                        ? '1 fråga saknar svar efter Word-importen.'
                        : `${unansweredImportedQuestions.length} frågor saknar svar efter Word-importen.`}
                    </strong>
                    {' '}Fyll i dem manuellt innan du skickar in formuläret.
                  </p>
                  <ul>
                    {unansweredImportedQuestions.map((unmatched) => (
                      <li key={unmatched.questionId}>
                        <button
                          type="button"
                          onClick={() => scrollToQuestion(unmatched.questionId)}
                          style={{
                            border: 0,
                            padding: 0,
                            background: 'none',
                            color: 'var(--ids-color-link, #005a9c)',
                            cursor: 'pointer',
                            font: 'inherit',
                            textDecoration: 'underline',
                            textAlign: 'left',
                          }}
                        >
                          {unmatched.questionText}
                        </button>
                      </li>
                    ))}
                  </ul>
                </IDSAlert>
              </div>
            )}
            {importedQuestionsMissingMotivation.length > 0 && (
              <div className="ids-mt-3">
                <IDSAlert type="attention">
                  <p>
                    <strong>
                      {importedQuestionsMissingMotivation.length === 1
                        ? '1 motivering saknas efter Word-importen.'
                        : `${importedQuestionsMissingMotivation.length} motiveringar saknas efter Word-importen.`}
                    </strong>
                    {' '}Komplettera motiveringen innan du skickar in formuläret.
                  </p>
                  <ul>
                    {importedQuestionsMissingMotivation.map((question) => (
                      <li key={question.id}>
                        <button
                          type="button"
                          onClick={() => scrollToQuestion(question.id)}
                          style={{
                            border: 0,
                            padding: 0,
                            background: 'none',
                            color: 'var(--ids-color-link, #005a9c)',
                            cursor: 'pointer',
                            font: 'inherit',
                            textDecoration: 'underline',
                            textAlign: 'left',
                          }}
                        >
                          Motivering till: {question.questionText}
                        </button>
                      </li>
                    ))}
                  </ul>
                </IDSAlert>
              </div>
            )}
            {importResult && (
              <div className="ids-mt-3">
                <IDSAlert type={importResult.importedAnswers > 0 ? 'info' : 'attention'}>
                  <p>
                    {importResult.importedAnswers} av {importResult.totalQuestions} svar kunde tolkas.
                    {' '}{(importResult.unmatchedQuestions?.length || 0) === 1
                      ? '1 fråga behöver fyllas i manuellt.'
                      : `${importResult.unmatchedQuestions?.length || 0} frågor behöver fyllas i manuellt.`}
                  </p>
                  {existingImportedAnswerCount > 0 && (
                    <p>
                      {existingImportedAnswerCount} importerade svar ignoreras
                      eftersom formuläret redan innehåller svar på frågorna.
                    </p>
                  )}
                  {importResult.importedAnswers > 0 && (
                    <p>
                      Granska samtliga importerade svar och säkerställ att de har hamnat
                      på rätt fråga innan du sparar eller skickar in formuläret.
                    </p>
                  )}
                  {importResult.warnings?.length > 0 && (
                    <ul>
                      {importResult.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                    </ul>
                  )}
                </IDSAlert>
                {importableAnswerCount > 0 && (
                  <div className="ids-mt-3">
                    <IDSAlert type="attention">
                      <p>
                        <strong>Gör detta först:</strong> Klicka på knappen nedan för att
                        fylla i de svar som hittades i Word-dokumentet. Börja därefter
                        fylla i frågorna som markeras med orange.
                      </p>
                    </IDSAlert>
                    <div className="ids-mt-2">
                      <IDSButton type="button" size="m" onClick={applyImportedAnswers}>
                        Fyll i {importableAnswerCount} importerade svar
                      </IDSButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </IDSCard>
      )}
      {sections
        .filter(isSectionVisible)
        .map((section, index) => {
          const isOpen = !!openSections[section.id];
          const sectionMissing = (section.questions || []).filter(isAnswerMissing).length;

          return (
            // overflow must stay clipped while closed — otherwise invisible absolutely-positioned
            // descendants (e.g. sr-only fieldset legends) still size themselves against this card
            // (its nearest positioned ancestor) and inflate the page well past the visible footer.
            <IDSCard className="ids-mb-3" style={{ overflow: isOpen ? 'visible' : 'hidden', position: 'relative', zIndex: isOpen ? 5 : 'auto' }}>
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
                aria-label={isOpen ? `Stäng avsnitt ${index + 1}` : `Öppna avsnitt ${index + 1}`}
                style={{
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '0.5rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textAlign: 'left',
                  outline: 'none',
                }}
              >
                <span
                  aria-hidden="true"
                  className={isOpen ? 'ids-icon-chevron-down' : 'ids-icon-chevron-right'}
                  style={{ height: '1rem', width: '1rem' }}
                />
                <h3 className="ids-heading-m" style={{ margin: 0, flex: 1, overflowWrap: 'anywhere' }}>
                  {section.title}
                </h3>
                <div style={{ flexShrink: 0 }}>
                  {sectionMissing > 0 ? (
                    <IDSBadge type="error">{sectionMissing}</IDSBadge>
                  ) : (
                    <IDSBadge type="success">Färdigt</IDSBadge>
                  )}
                </div>
              </button>

              <Collapse open={isOpen}>
                <div style={{ borderTop: '1px solid var(--ids-palette-neutral-90, #dfe5ea)', padding: '1rem 0 0' }}>
                  <LinkifiedText className="ids-body ids-mb-4">
                    {section.description}
                  </LinkifiedText>

                  {section.questions?.map((question) => {
                    const answer = answers[question.id]?.answer;
                    const followupText = answers[question.id]?.followupText || "";
                    const textValue = answers[question.id]?.textValue || "";
                    const motivation = answers[question.id]?.motivation || "";
                    const isTextQuestion = question.questionType === 'TEXT';
                    const isDateQuestion = question.questionType === 'DATE';
                    const isTextBlock = question.questionType === 'TEXT_BLOCK';
                    const isSourceSystemQuestion = question.questionType === 'SOURCE_SYSTEM';
                    const isServiceProviderQuestion = question.questionType === 'SERVICE_PROVIDER';
                    const isYesNoQuestion = !isTextQuestion && !isDateQuestion && !isTextBlock
                      && !isSourceSystemQuestion && !isServiceProviderQuestion;
                    const missingAfterImport = unansweredImportedQuestions.some(
                      (unmatched) => unmatched.questionId === question.id
                    );
                    const motivationMissingAfterImport = importedQuestionsMissingMotivation.some(
                      (candidate) => candidate.id === question.id
                    );
                    const showFollowup = isYesNoQuestion && isFollowupVisible(question, answer);
                    const answerMatchesExpected = isYesNoQuestion &&
                      question.expectedAnswerBoolean !== null &&
                      question.expectedAnswerBoolean !== undefined &&
                      answer === question.expectedAnswerBoolean;
                    const unexpected = isYesNoQuestion && isAnswerUnexpected(question, answer);
                    const showOptionalComment = answerMatchesExpected && (optionalCommentVisible[question.id] || Boolean((motivation || '').trim()));

                    if (isTextBlock) {
                      return (
                        <div
                          key={question.id}
                          className="ids-mb-5"
                          style={{
                            borderLeft: '3px solid var(--ids-color-primary, #0065bd)',
                            paddingLeft: '0.9rem',
                            marginTop: '1.25rem',
                          }}
                        >
                          <p
                            style={{
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              marginBottom: question.bodyText ? '0.3rem' : 0,
                            }}
                          >
                            {question.questionTitle || question.questionText}
                            <InfoTooltip info={question.info} />
                          </p>

                          {question.bodyText && (
                            <MarkdownContent
                              style={{
                                color: 'var(--ids-palette-neutral-30)',
                                fontSize: '0.875rem',
                              }}
                            >
                              {question.bodyText}
                            </MarkdownContent>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={question.id}
                        id={`question-${question.id}`}
                        className="ids-mb-4"
                        tabIndex={missingAfterImport || motivationMissingAfterImport ? -1 : undefined}
                        style={missingAfterImport ? {
                          borderLeft: '4px solid #d97706',
                          borderRadius: '0 4px 4px 0',
                          background: '#fff7ed',
                          padding: '0.9rem 1rem',
                        } : undefined}
                      >
                        {missingAfterImport && (
                          <div
                            role="status"
                            className="ids-mb-2"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#9a3412',
                              fontWeight: 600,
                            }}
                          >
                            <span className="ids-icon-attention" aria-hidden="true" />
                            Svar saknas efter Word-import
                          </div>
                        )}
                        {question.questionTitle && (
                          <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.2rem' }}>
                            {question.questionTitle}
                          </p>
                        )}
                        <p className="ids-mb-2">
                          {question.questionText}
                          {question.mandatory !== false && (
                            <span style={{ color: 'var(--ids-palette-error-40, #c00)' }} title="Obligatorisk"> *</span>
                          )}
                          <InfoTooltip info={question.info} />
                        </p>

                        {isTextQuestion || isServiceProviderQuestion ? (
                          <IDSTextarea
                            block
                            rows={3}
                            value={textValue}
                            onInput={(e) => handleTextValueChange(question.id, e.target.value)}
                            placeholder="Ange svar..."
                          />
                        ) : isDateQuestion ? (
                          <div style={{ position: 'relative', zIndex: 3000, isolation: 'isolate' }}>
                            <IDSDatePicker
                              value={textValue}
                              onChange={(e) => handleTextValueChange(question.id, e.value)}
                            />
                          </div>
                        ) : isSourceSystemQuestion ? (
                          <SourceSystemPicker
                            value={textValue}
                            options={sourceSystemOptions}
                            loading={sourceSystemsLoading}
                            error={sourceSystemsError}
                            onChange={(value) => handleTextValueChange(question.id, value)}
                          />
                        ) : (
                          <IDSRadioGroup
                            legend={question.questionText}
                            hideLegend
                            onRadioChange={(e) => handleAnswerChange(question.id, e.target.value === "true")}
                          >
                            <IDSRadio value="true" checked={answer === true}>Ja</IDSRadio>
                            <IDSRadio value="false" checked={answer === false}>Nej</IDSRadio>
                          </IDSRadioGroup>
                        )}

                        {question.followupConfig?.text && !isTextQuestion && !isDateQuestion && (
                          <Collapse open={showFollowup}>
                            <div className="ids-mt-4">
                              <IDSInput
                                label={`${question.followupConfig.text} *`}
                                type="text"
                                maxLength={400}
                                value={followupText}
                                onChange={(e) => handleFollowupChange(question.id, e.target.value)}
                                placeholder="Ange svar..."
                              />
                            </div>
                          </Collapse>
                        )}

                        {!isTextQuestion && !isDateQuestion && answerMatchesExpected && !showOptionalComment && (
                          <div className="ids-mt-3">
                            <IDSButton
                              type="button"
                              size="s"
                              secondary
                              onClick={() => setOptionalCommentVisible((prev) => ({ ...prev, [question.id]: true }))}
                            >
                              + Lägg till kommentar (valfritt)
                            </IDSButton>
                          </div>
                        )}

                        {!isTextQuestion && !isDateQuestion && showOptionalComment && (
                          <div className="ids-mt-4">
                            <IDSTextarea
                              block
                              rows={3}
                              label="Övrig kommentar"
                              value={motivation}
                              onChange={(e) => handleMotivationChange(question.id, e.target.value)}
                              placeholder="Lägg till övrig kommentar..."
                            />
                          </div>
                        )}

                        {!isTextQuestion && !isDateQuestion && (
                          <Collapse open={unexpected}>
                            <div
                              className="ids-mt-3"
                              style={motivationMissingAfterImport ? {
                                borderLeft: '4px solid #d97706',
                                borderRadius: '0 4px 4px 0',
                                background: '#fff7ed',
                                padding: '0.75rem 1rem',
                              } : undefined}
                            >
                              {motivationMissingAfterImport && (
                                <div
                                  role="status"
                                  className="ids-mb-2"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#9a3412',
                                    fontWeight: 600,
                                  }}
                                >
                                  <span className="ids-icon-attention" aria-hidden="true" />
                                  Motivering saknas efter Word-import
                                </div>
                              )}
                              <IDSTextarea
                                block
                                label="Ange motivering *"
                                rows={3}
                                value={motivation}
                                onChange={(e) => handleMotivationChange(question.id, e.target.value)}
                                placeholder="Beskriv varför svaret avviker från förväntat..."
                              />
                            </div>
                          </Collapse>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </IDSCard>
          );
        })}
      <div className="ids-mt-6" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <IDSButton size="m" onClick={handleSaveAnswers} disabled={saving || submittingReport}>
          {saving ? "Sparar..." : "Spara svar"}
        </IDSButton>
        {/* Show manual-save feedback only when autosave is idle (avoids two indicators at once) */}
        {autosaveStatus === 'idle' && saveSuccess && (
          <IDSAlert type="success" compact><p>Svar sparade!</p></IDSAlert>
        )}
        {autosaveStatus === 'idle' && saveError && (
          <IDSAlert type="error" compact><p>{saveError}</p></IDSAlert>
        )}
        <LastUpdated value={lastUpdatedAt} label="Dina svar sparades senast" />
      </div>
      {/* Fixed top-right indicator — always visible regardless of scroll position */}
      <AutosaveIndicator status={autosaveStatus} fixed />

      {/* A conflict means somebody else wrote to the same answers. Autosave is already
          stopped at this point; reloading is the only safe way forward, so the dialog
          cannot be dismissed into a state where the user keeps typing into stale data. */}
      <ConfirmDialog
        isOpen={!!conflict}
        title="Svaren har ändrats av någon annan"
        confirmLabel="Ladda om sidan"
        cancelLabel="Fortsätt utan att spara"
        onConfirm={() => window.location.reload()}
        onCancel={() => setConflict(null)}
      >
        <div style={{ marginBottom: '1.5rem', color: '#333' }}>
          <p style={{ marginTop: 0 }}>{conflict}</p>
          <p style={{ marginBottom: 0 }}>
            Automatiskt sparande är pausat så att dina ändringar inte skriver över någon annans.
            Ladda om sidan för att hämta den senaste versionen — observera att ändringar du gjort
            sedan senaste sparningen då går förlorade.
          </p>
        </div>
      </ConfirmDialog>

      <div className="ids-mt-6">
        <IDSButton size="m"
          disabled={missingSections.length > 0 || !allFailedTestsJustified || isInReview || submittingReport}
          onClick={() => setShowSubmitConfirm(true)}
        >
          {submittingReport ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <IDSSpinner size="s" />
              Skickar...
            </span>
          ) : 'Skapa testrapport'}
        </IDSButton>

        <ConfirmDialog
          isOpen={showSubmitConfirm}
          title="Skicka in underlaget för granskning?"
          confirmLabel={submittingReport ? 'Skickar in...' : 'Ja, skicka in'}
          cancelLabel="Avbryt"
          confirmDisabled={submittingReport}
          onCancel={() => setShowSubmitConfirm(false)}
          onConfirm={performSubmit}
        >
          <div style={{ marginBottom: '1.5rem', color: '#333' }}>
            <p style={{ marginTop: 0 }}>
              Endast <strong>en deklaration per organisation</strong> tas emot. Det underlag du
              skickar in blir organisationens deklaration för det här tjänstekontraktet.
            </p>
            <p style={{ marginBottom: 0 }}>
              När underlaget är inskickat kan du inte ändra svaren eller skicka in ett nytt
              underlag förrän granskningen är avslutad.
            </p>
          </div>
        </ConfirmDialog>

        {submitError && (
          <div className="ids-mt-2">
            <IDSAlert type="error"><p>{submitError}</p></IDSAlert>
          </div>
        )}
        {isInReview && (
          <div className="ids-mt-2">
            <IDSAlert type="attention">
              <p>En rapport är redan inskickad för granskning. Du kan inte skicka in en ny rapport förrän den pågående granskningen är avslutad.</p>
            </IDSAlert>
          </div>
        )}
        {!isInReview && (!allFailedTestsJustified || missingSections.length > 0) && (
          <div className="ids-mt-2" style={{ color: "var(--ids-palette-error-40)", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            {!allFailedTestsJustified && (
              <span>Alla misslyckade och/eller ej körda testfall måste ha en motivering.</span>
            )}
            {missingSections.length > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                Alla obligatoriska frågor, följdfrågor och motiveringar måste fyllas i.
                <IDSTooltip
                  position="top"
                  maxWidth=""
                  trigger={
                    <span
                      aria-label="Visa vilka avsnitt som saknar svar"
                      className="ids-icon-information ids-icon--m ids-icon--color-preset-1 ids-icon--interactive"
                      role="button"
                      style={{ position: "relative", zIndex: 4 }}
                      tabIndex={0}
                    />
                  }
                >
                  <strong>Saknar svar i:</strong>
                  <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                    {missingSections.map((s) => (
                      <li key={s.title}>{s.title} ({s.count})</li>
                    ))}
                  </ul>
                </IDSTooltip>
              </span>
            )}
          </div>
        )}
      </div>
      </div>

    </div>
  );
};

export default QuestionSection;
